import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import {
  CalendarClock,
  CalendarX,
  CheckCircle,
  Eye,
  MoreHorizontal,
  Pencil,
  UserCheck,
  UserX,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

const NON_EDITABLE_STATUSES = new Set(['completed', 'cancelled', 'no_show']);

interface AppointmentActionsMenuProps {
  status: string;
  onViewDetail?: () => void;
  onEdit?: () => void;
  onReschedule?: () => void;
  onConfirm?: () => void;
  onCheckIn?: () => void;
  onStart?: () => void;
  onCheckOut?: () => void;
  onCancel?: () => void;
  onNoShow?: () => void;
}

interface ActionItem {
  label: string;
  icon: typeof CheckCircle;
  action: string;
  show: boolean;
  variant?: 'default' | 'destructive';
  onClick?: () => void;
}

function MenuSection({
  label,
  items,
  onSelect,
}: {
  label?: string;
  items: ActionItem[];
  onSelect: (item: ActionItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="py-1">
      {label ? (
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
          {label}
        </div>
      ) : null}
      {items.map((item) => (
        <Button
          key={item.action}
          type="button"
          variant="ghost"
          onClick={() => onSelect(item)}
          className={cn(
            'w-full justify-start gap-2 rounded-lg px-3 py-2 text-sm',
            item.variant === 'destructive'
              ? 'text-coral-600 hover:bg-coral-50'
              : 'text-[var(--text-primary)] hover:bg-[var(--bg-surface-muted)]',
          )}
        >
          <item.icon className="size-4" />
          {item.label}
        </Button>
      ))}
    </div>
  );
}

export function AppointmentActionsMenu({
  status,
  onViewDetail,
  onEdit,
  onReschedule,
  onConfirm,
  onCheckIn,
  onStart,
  onCheckOut,
  onCancel,
  onNoShow,
}: AppointmentActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const canChangeSchedule = !NON_EDITABLE_STATUSES.has(status);

  const navigation = (
    [
      {
        label: 'View Detail',
        icon: Eye,
        action: 'viewDetail',
        show: !!onViewDetail,
        onClick: onViewDetail,
      },
      {
        label: 'Edit Appointment',
        icon: Pencil,
        action: 'edit',
        show: canChangeSchedule && !!onEdit,
        onClick: onEdit,
      },
      {
        label: 'Reschedule',
        icon: CalendarClock,
        action: 'reschedule',
        show: canChangeSchedule && !!onReschedule,
        onClick: onReschedule,
      },
    ] as ActionItem[]
  ).filter((a) => a.show);

  const statusItems = (
    [
      {
        label: 'Confirm',
        icon: CheckCircle,
        action: 'confirm',
        show: status === 'scheduled' && !!onConfirm,
        onClick: onConfirm,
      },
      {
        label: 'Check In',
        icon: UserCheck,
        action: 'checkIn',
        show: status === 'confirmed' && !!onCheckIn,
        onClick: onCheckIn,
      },
      {
        label: 'Start',
        icon: CheckCircle,
        action: 'start',
        show: status === 'checked_in' && !!onStart,
        onClick: onStart,
      },
      {
        label: 'Complete',
        icon: UserX,
        action: 'checkOut',
        show: status === 'in_progress' && !!onCheckOut,
        onClick: onCheckOut,
      },
    ] as ActionItem[]
  ).filter((a) => a.show);

  const destructive = (
    [
      {
        label: 'Cancel',
        icon: XCircle,
        action: 'cancel',
        show: ['scheduled', 'confirmed', 'checked_in', 'in_progress'].includes(status) && !!onCancel,
        variant: 'destructive' as const,
        onClick: onCancel,
      },
      {
        label: 'No Show',
        icon: CalendarX,
        action: 'noShow',
        show: ['scheduled', 'confirmed'].includes(status) && !!onNoShow,
        variant: 'destructive' as const,
        onClick: onNoShow,
      },
    ] as ActionItem[]
  ).filter((a) => a.show);

  if (navigation.length === 0 && statusItems.length === 0 && destructive.length === 0) {
    return null;
  }

  const handleSelect = (item: ActionItem) => {
    setOpen(false);
    item.onClick?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 rounded-lg text-[var(--text-secondary)]"
          aria-label="Appointment actions"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={4}
        className="z-[100] w-[200px] p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <MenuSection items={navigation} onSelect={handleSelect} />
        {statusItems.length > 0 && navigation.length > 0 ? (
          <div className="mx-2 border-t border-[var(--border-subtle)]" />
        ) : null}
        <MenuSection label="Status" items={statusItems} onSelect={handleSelect} />
        {destructive.length > 0 && (navigation.length > 0 || statusItems.length > 0) ? (
          <div className="mx-2 border-t border-[var(--border-subtle)]" />
        ) : null}
        <MenuSection items={destructive} onSelect={handleSelect} />
      </PopoverContent>
    </Popover>
  );
}
